"use client";

import React, { useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Grid,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon, Chat as ChatIcon } from "@mui/icons-material";
import { MessageItem } from "../TicketComponents/MessageItem";
import { MessageInput } from "../TicketComponents/MessageInput";
import { StatusChip } from "../TicketComponents/StatusChip";
import { DialogWrapper } from '../DialogWrapper';
import { useEscKey } from '../../../hooks/useEscKey';

export interface MessageDialogProps {
  open: boolean;
  onClose: () => void;
  selectedTicket: any;
  authUser: any;
  messages: any[];
  messagesLoading: boolean;
  newMessage: string;
  setNewMessage: (message: string) => void;
  messageFile: File | null;
  setMessageFile: (file: File | null) => void;
  sendingMessage: boolean;
  onSendMessage: () => void;
  onMessageFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveMessageFile: () => void;
  onDownloadMessageFile: (message: any) => void;
}

export const MessageDialog: React.FC<MessageDialogProps> = ({
  open,
  onClose,
  selectedTicket,
  authUser,
  messages,
  messagesLoading,
  newMessage,
  setNewMessage,
  messageFile,
  setMessageFile,
  sendingMessage,
  onSendMessage,
  onMessageFileSelect,
  onRemoveMessageFile,
  onDownloadMessageFile,
}) => {
  const textFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null); // ✅ Scroll için ref
  const [localMessage, setLocalMessage] = React.useState(newMessage);

  useEscKey (open, onClose)
  // Parent state ile local state'i hafif gecikmeyle senkronla
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (localMessage !== newMessage) setNewMessage(localMessage);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [localMessage, newMessage, setNewMessage]);

  // ✅ Mesajlar değiştiğinde en son mesaja scroll yap
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]); // messages değiştiğinde çalışır

  // ✅ Mesaj gönderildikten sonra input'u temizle
  const handleSendMessage = async () => {
    await onSendMessage();
    // Mesaj gönderildikten sonra input'u temizle
    setLocalMessage("");
    setNewMessage("");
  
    setTimeout(() => {
      textFieldRef.current?.focus();
    },100);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalMessage(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClose = () => {
    setLocalMessage("");
    setNewMessage("");
    setMessageFile(null);
    onClose();
  };

  // Açılınca input/textarea'yı odakla
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => textFieldRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [open]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
      sx={{
        "& .MuiDialog-paper": {
          height: "85vh",
          maxHeight: "85vh",
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #2c7873 0%, #004d40 100%)",
          color: "white",
          py: 2.5,
          pb: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flex: 1 }}>
            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mt: 0.5,
              }}
            >
              <ChatIcon sx={{ fontSize: 24 }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: "1.4rem" }}>
                Talep Notları
              </Typography>

              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      label={`#${selectedTicket?.id}`}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.9)",
                        color: "#004d40",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, fontSize: "0.95rem", lineHeight: 1.3 }}
                    >
                      {selectedTicket?.subject}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.8, fontWeight: 600, fontSize: "0.8rem", minWidth: "45px" }}
                    >
                      🏢 Firma:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.9, fontWeight: 700, fontSize: "0.8rem" }}
                    >
                      {selectedTicket?.company_name}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.8, fontWeight: 600, fontSize: "0.8rem", minWidth: "45px" }}
                    >
                      📊 Durum:
                    </Typography>
                    <Box
                      sx={{
                        transform: "scale(0.8)",
                        transformOrigin: "left center",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <StatusChip statusId={selectedTicket?.status_id} statuses={[]} />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <IconButton
            onClick={handleClose}
            sx={{
              color: "white",
              backgroundColor: "rgba(255,255,255,0.1)",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.2)",
                transform: "scale(1.1)",
              },
              transition: "all 0.2s ease",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          p: 0,
        }}
      >
        {/* Not Listesi */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            overflow: "auto",
            background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {messagesLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                flexDirection: "column",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography color="text.secondary">Notlar yükleniyor...</Typography>
            </Box>
          ) : messages.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "grey.100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <ChatIcon sx={{ fontSize: 40, color: "grey.400" }} />
              </Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Henüz not bulunmuyor
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {messages.map((message: any) => (
                <MessageItem
                  key={message.id}
                  message={message}
                  authUser={authUser}
                  onDownloadMessageFile={onDownloadMessageFile}
                />
              ))}
              {/* ✅ Scroll için boş div - en son mesajda kalmasını sağlar */}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        {/* Not Giriş Alanı */}
        <MessageInput
          ref={textFieldRef}
          messageFile={messageFile}
          localMessage={localMessage} // ✅ Artık gönderildikten sonra temizlenecek
          onMessageChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          sendingMessage={sendingMessage}
          onMessageFileSelect={onMessageFileSelect}
          onRemoveMessageFile={onRemoveMessageFile}
          onSendMessage={handleSendMessage} // ✅ Yeni fonksiyonu kullan
          formatFileSize={formatFileSize}
        />
      </DialogContent>
    </DialogWrapper>
  );
};